// EL EFECTO DEL FONDO DE /login (t264d, 16-oct-2026)
//
// El dueño: «añade algún código de estos modernos que salgan como pulsaciones y cosas chulas al mover el ratón o hacer clic».
// Tres cosas, discretas (el formulario sigue siendo lo que más se ve):
//   · un HALO de luz suave que sigue al ratón por el fondo, con un poco de inercia;
//   · al hacer clic o tocar, una PULSACIÓN: dos ondas del color de la marca que se abren y se desvanecen;
//   · los PUNTOS de la trama cerca del cursor se iluminan y se apartan un poco (la misma rejilla de 22 px que la trama).
// CÓMO, sin librerías: un <canvas> encima del fondo y DEBAJO del contenido, con `pointer-events: none` (los clics van al
// formulario, nunca a la capa), y requestAnimationFrame solo mientras hay algo que mover: quieto, no pinta ni gasta nada. Se
// para con la pestaña oculta. Con «reducir movimiento» del sistema no se monta (queda el fondo quieto). Se monta DESPUÉS de
// pintar la pantalla (en un efecto), así que no retrasa la entrada ni mueve nada de sitio. En el móvil y la tableta, los
// toques: la onda donde se toca, y el halo mientras se arrastra el dedo. El color es el de Biango (utils/contraste), nunca
// el de una clínica: esta pantalla se pinta antes de saber de cuál se entra.
// Vive en utils/ (con la marca de abajo) para que las guardias que montan /login lo carguen DE VERDAD sin ponerle un doble:
//  @pintarJsx-con-react
import { useEffect, useRef, useState } from 'react'
import { AZUL_MARCA } from './contraste'

export const CELESTE_EFECTO = '#90CAF9'
export const PASO_TRAMA = 22
export const DURACION_ONDA = 1000
export const RADIO_HALO = 280
export const RADIO_PUNTOS = 160
const MAX_ONDAS = 6

// «#1E88E5» + alfa → «rgba(30, 136, 229, 0.2)»: el lienzo no entiende las variables CSS.
export function rgba(hex, a) {
  const h = String(hex || '').replace('#', '')
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16)
  return 'rgba(' + ((n >> 16) & 255) + ', ' + ((n >> 8) & 255) + ', ' + (n & 255) + ', ' + Math.max(0, Math.min(1, a)) + ')'
}

// ¿Se puede (y se debe) animar aquí? No con «reducir movimiento», ni sin requestAnimationFrame o sin lienzo.
export function puedeAnimar(win) {
  try {
    if (!win || typeof win.requestAnimationFrame !== 'function') return false
    if (win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches) return false
    const d = win.document
    const c = d && d.createElement && d.createElement('canvas')
    return !!(c && c.getContext && c.getContext('2d'))
  } catch (_) { return false }
}

const salida = (p) => 1 - Math.pow(1 - p, 3)

// EL MOTOR, sin DOM (lo prueba la guardia con un lienzo de mentira). `pintar(ctx, t)` dibuja un fotograma y dice si hace
// falta otro: si nada se mueve, false, y el bucle se para solo.
export function crearMotor({ color = AZUL_MARCA, colorPunto = CELESTE_EFECTO, paso: pasoBase = PASO_TRAMA } = {}) {
  const st = { ancho: 0, alto: 0, z: 1, objetivo: null, halo: { x: 0, y: 0, alfa: 0 }, visible: false, ondas: [] }
  return {
    estado: st,
    medir(ancho, alto) { st.ancho = ancho; st.alto = alto },
    // Con el tamaño de letra del perfil (el zoom de la página), la trama de CSS crece con él: la rejilla de los puntos, también.
    escala(z) { st.z = z > 0 ? z : 1 },
    mover(x, y) {
      st.objetivo = { x, y }
      if (st.halo.alfa < 0.02) { st.halo.x = x; st.halo.y = y }   // aparece donde está el cursor, no viene desde una esquina
      st.visible = true
    },
    salir() { st.visible = false },
    tocar(x, y, t) {
      st.ondas.push({ x, y, t0: t })
      if (st.ondas.length > MAX_ONDAS) st.ondas.shift()
    },
    vaciar() { st.ondas = []; st.visible = false; st.halo.alfa = 0 },
    pintar(ctx, t) {
      ctx.clearRect(0, 0, st.ancho, st.alto)
      const h = st.halo
      let falta = false
      if (st.objetivo) {
        const dx = st.objetivo.x - h.x, dy = st.objetivo.y - h.y
        h.x += dx * 0.18; h.y += dy * 0.18
        if (Math.abs(dx) + Math.abs(dy) > 0.6) falta = true
      }
      const meta = st.visible ? 1 : 0
      if (Math.abs(h.alfa - meta) > 0.01) { h.alfa += (meta - h.alfa) * 0.12; falta = true } else h.alfa = meta
      if (h.alfa > 0.01) {
        const g = ctx.createRadialGradient(h.x, h.y, 0, h.x, h.y, RADIO_HALO)
        g.addColorStop(0, rgba(color, 0.22 * h.alfa))
        g.addColorStop(1, rgba(color, 0))
        ctx.fillStyle = g
        ctx.fillRect(h.x - RADIO_HALO, h.y - RADIO_HALO, RADIO_HALO * 2, RADIO_HALO * 2)
        // Los puntos de la trama que caen cerca (la trama de CSS pone uno en el centro de cada casilla de `paso`).
        const paso = pasoBase * st.z
        const c0 = Math.max(0, Math.floor((h.x - RADIO_PUNTOS) / paso)), c1 = Math.ceil((h.x + RADIO_PUNTOS) / paso)
        const f0 = Math.max(0, Math.floor((h.y - RADIO_PUNTOS) / paso)), f1 = Math.ceil((h.y + RADIO_PUNTOS) / paso)
        for (let c = c0; c <= c1; c++) {
          for (let f = f0; f <= f1; f++) {
            const px = c * paso + paso / 2, py = f * paso + paso / 2
            const ddx = px - h.x, ddy = py - h.y
            const d = Math.sqrt(ddx * ddx + ddy * ddy)
            if (d >= RADIO_PUNTOS) continue
            const k = Math.pow(1 - d / RADIO_PUNTOS, 2)
            const empuje = d > 0.5 ? (5 * k) / d : 0
            ctx.fillStyle = rgba(colorPunto, 0.6 * k * h.alfa)
            ctx.beginPath()
            ctx.arc(px + ddx * empuje, py + ddy * empuje, 1.1 + 1.1 * k, 0, Math.PI * 2)
            ctx.fill()
          }
        }
      }
      // Las ondas de cada clic: dos anillos, el segundo un poco después.
      st.ondas = st.ondas.filter((o) => t - o.t0 < DURACION_ONDA + 160)
      for (const o of st.ondas) {
        for (const retraso of [0, 140]) {
          const p = (t - o.t0 - retraso) / DURACION_ONDA
          if (p <= 0 || p >= 1) continue
          ctx.strokeStyle = rgba(retraso ? colorPunto : color, (retraso ? 0.3 : 0.42) * Math.pow(1 - p, 1.4))
          ctx.lineWidth = retraso ? 1.5 : 2
          ctx.beginPath()
          ctx.arc(o.x, o.y, 10 + 210 * salida(p), 0, Math.PI * 2)
          ctx.stroke()
        }
      }
      if (st.ondas.length) falta = true
      return falta
    },
  }
}

export default function EfectoEntrar({ color = AZUL_MARCA, colorPunto = CELESTE_EFECTO, onMedida = null }) {
  const ref = useRef(null)
  const [activo, setActivo] = useState(false)
  useEffect(() => { if (puedeAnimar(typeof window === 'undefined' ? null : window)) setActivo(true) }, [])
  useEffect(() => {
    if (!activo) return undefined
    const lienzo = ref.current
    const ctx = lienzo && lienzo.getContext && lienzo.getContext('2d')
    if (!ctx) return undefined
    const motor = crearMotor({ color, colorPunto })
    let raf = 0
    let rect = { left: 0, top: 0 }
    const dpr = () => Math.min(2, window.devicePixelRatio || 1)
    const medir = () => {
      const r = lienzo.getBoundingClientRect()
      rect = r
      const d = dpr()
      lienzo.width = Math.max(1, Math.round(r.width * d))
      lienzo.height = Math.max(1, Math.round(r.height * d))
      ctx.setTransform(d, 0, 0, d, 0, 0)
      motor.medir(r.width, r.height)
      let z = 1
      try { z = parseFloat(document.documentElement.style.zoom) || 1 } catch (_) { z = 1 }
      motor.escala(z)
    }
    const fotograma = (t) => {
      raf = 0
      const t0 = onMedida ? performance.now() : 0
      const sigue = motor.pintar(ctx, t)
      if (onMedida) onMedida(performance.now() - t0)
      if (sigue && !document.hidden) raf = window.requestAnimationFrame(fotograma)
    }
    const pedir = () => { if (!raf && !document.hidden) raf = window.requestAnimationFrame(fotograma) }
    const punto = (e) => { rect = lienzo.getBoundingClientRect(); return [e.clientX - rect.left, e.clientY - rect.top] }
    const alMover = (e) => {
      if (e.pointerType === 'touch' && !e.pressure && e.buttons === 0) return
      const [x, y] = punto(e); motor.mover(x, y); pedir()
    }
    const alPulsar = (e) => {
      const [x, y] = punto(e); motor.mover(x, y); motor.tocar(x, y, performance.now()); pedir()
    }
    const alSoltar = (e) => { if (e.pointerType === 'touch') { motor.salir(); pedir() } }
    const alSalir = () => { motor.salir(); pedir() }
    const alOcultar = () => {
      if (document.hidden) { if (raf) window.cancelAnimationFrame(raf); raf = 0; motor.vaciar(); ctx.clearRect(0, 0, lienzo.width, lienzo.height) }
    }
    medir()
    const op = { passive: true }
    window.addEventListener('pointermove', alMover, op)
    window.addEventListener('pointerdown', alPulsar, op)
    window.addEventListener('pointerup', alSoltar, op)
    window.addEventListener('pointercancel', alSalir, op)
    document.addEventListener('mouseleave', alSalir, op)
    window.addEventListener('blur', alSalir)
    window.addEventListener('resize', medir)
    window.addEventListener('biango:zoom', medir)
    document.addEventListener('visibilitychange', alOcultar)
    return () => {
      if (raf) window.cancelAnimationFrame(raf)
      window.removeEventListener('pointermove', alMover, op)
      window.removeEventListener('pointerdown', alPulsar, op)
      window.removeEventListener('pointerup', alSoltar, op)
      window.removeEventListener('pointercancel', alSalir, op)
      document.removeEventListener('mouseleave', alSalir, op)
      window.removeEventListener('blur', alSalir)
      window.removeEventListener('resize', medir)
      window.removeEventListener('biango:zoom', medir)
      document.removeEventListener('visibilitychange', alOcultar)
    }
  }, [activo])
  if (!activo) return null
  return (
    <canvas ref={ref} aria-hidden="true" data-efecto-entrar="" style={{
      position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0, display: 'block',
    }} />
  )
}
